import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { createQuote, updateQuote, fetchQuote } from "../../store/slices/quotesSlice";
import {
  searchCruises,
  fetchOperators,
  fetchCruiseDetail,
  clearCruiseDetail,
} from "../../store/slices/cruisesSlice";
import { formatCurrency, formatDate } from "../../utils/format";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  StarIcon,
  CheckIcon,
  ChevronRightIcon,
  MapPinIcon,
  CalendarDaysIcon,
  MoonIcon,
  GlobeAltIcon,
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ShipIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";

const STEPS = ["Select Cruise", "Cabin & Pricing", "Client Details", "Review & Send"];

// Region options for the Widgety API
const REGIONS = [
  "Alaska", "Arctic", "Asia", "Australasia", "Baltic", "British Isles",
  "Canada", "Canary Islands", "Caribbean", "Central America",
  "East Coast US", "Hawaii", "Indian Ocean", "Mediterranean",
  "Mexico", "Middle East", "Northern Europe", "Panama Canal",
  "Rhine", "Danube", "South America", "South Pacific",
  "Transatlantic", "West Coast US", "Western Europe", "World Cruise",
];

function CruiseCard({ cruise, onSelect, operatorLogos }) {
  const logoUrl = operatorLogos[cruise.operatorSlug];
  const departDate = new Date(cruise.startsOn);
  const returnDate = new Date(cruise.endsOn);

  return (
    <button
      onClick={() => onSelect(cruise)}
      className="w-full text-left bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all overflow-hidden group"
    >
      <div className="p-5">
        {/* Header: Operator + Ship */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="h-8 w-8 rounded-lg object-contain flex-shrink-0 bg-gray-50 p-0.5"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : (
              <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <GlobeAltIcon className="h-4 w-4 text-indigo-600" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs font-medium text-indigo-600 uppercase tracking-wider truncate">
                {cruise.operatorTitle}
              </p>
              <h3 className="text-base font-bold text-gray-900 truncate">{cruise.shipTitle}</h3>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-indigo-500" />
          </div>
        </div>

        {/* Itinerary name */}
        <p className="text-sm font-medium text-gray-700 mb-3 line-clamp-1">{cruise.name}</p>

        {/* Details row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1">
            <CalendarDaysIcon className="h-3.5 w-3.5" />
            {departDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })}
          </span>
          <span className="inline-flex items-center gap-1">
            <MoonIcon className="h-3.5 w-3.5" />
            {cruise.cruiseNights} nights
          </span>
          {cruise.regions.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <GlobeAltIcon className="h-3.5 w-3.5" />
              {cruise.regions.slice(0, 2).join(", ")}
            </span>
          )}
          {cruise.cruiseType.length > 0 && (
            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-medium text-gray-600">
              {cruise.cruiseType[0]}
            </span>
          )}
        </div>

        {/* Route */}
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
          <MapPinIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
          <span className="truncate">{cruise.startsAt}</span>
          <ArrowPathIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
          <span className="truncate">{cruise.endsAt}</span>
        </div>

        {/* Cabin type availability indicators */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {cruise.insidePrice > 0 && (
            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-medium text-gray-600">Inside</span>
          )}
          {cruise.outsidePrice > 0 && (
            <span className="px-2 py-0.5 bg-blue-50 rounded text-xs font-medium text-blue-600">Outside</span>
          )}
          {cruise.balconyPrice > 0 && (
            <span className="px-2 py-0.5 bg-indigo-50 rounded text-xs font-medium text-indigo-600">Balcony</span>
          )}
          {cruise.suitePrice > 0 && (
            <span className="px-2 py-0.5 bg-purple-50 rounded text-xs font-medium text-purple-600">Suite</span>
          )}
        </div>
      </div>

      {/* Hover indicator */}
      <div className="px-5 py-2 bg-gray-50 group-hover:bg-indigo-50 transition-colors flex items-center justify-between">
        <span className="text-xs text-gray-500 group-hover:text-indigo-600 font-medium">
          Select this cruise
        </span>
        <ChevronRightIcon className="h-4 w-4 text-gray-400 group-hover:text-indigo-600" />
      </div>
    </button>
  );
}

function PortVisitTimeline({ portVisits }) {
  if (!portVisits || portVisits.length === 0) return null;

  return (
    <div className="mt-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">Itinerary</h4>
      <div className="space-y-0">
        {portVisits.map((visit, idx) => {
          const portName = visit.port?.name || visit.name || "At Sea";
          const country = visit.port?.country || "";
          const image = visit.portDetails?.images?.[0]?.href;
          const isFirst = idx === 0;
          const isLast = idx === portVisits.length - 1;

          return (
            <div key={idx} className="flex gap-3">
              {/* Timeline line + dot */}
              <div className="flex flex-col items-center w-6 flex-shrink-0">
                <div
                  className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${
                    isFirst || isLast
                      ? "bg-indigo-600 border-indigo-600"
                      : visit.port
                      ? "bg-white border-indigo-400"
                      : "bg-gray-200 border-gray-300"
                  }`}
                />
                {!isLast && <div className="w-0.5 flex-1 bg-gray-200 min-h-[2rem]" />}
              </div>

              {/* Content */}
              <div className="flex items-start gap-3 pb-4 min-w-0 flex-1">
                {image && (
                  <img
                    src={image}
                    alt={portName}
                    className="h-12 w-16 rounded-lg object-cover flex-shrink-0"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{portName}</p>
                  {country && <p className="text-xs text-gray-500">{country}</p>}
                  {visit.port && visit.arrives_on && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Day {visit.day}
                      {visit.arrives_on && ` \u00b7 Arrives ${new Date(visit.arrives_on).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CabinSelector({ fareSets, cabinTypes, selectedCabins, onToggleFare, onAddCustom, detailStatus }) {
  // Build a grade_code -> cabinType lookup
  const gradeMap = useMemo(() => {
    const map = {};
    (cabinTypes || []).forEach((ct) => {
      (ct.gradeCodes || []).forEach((code) => {
        map[code] = ct;
      });
    });
    return map;
  }, [cabinTypes]);

  // Collect all unique fares from the first fare set (cruise-only, no airport)
  // Prefer the fare set without an airport code
  const primaryFareSet = fareSets.find((fs) => !fs.airport) || fareSets[0];
  const fares = primaryFareSet?.fares || [];

  // Group fares by their cabin type
  const groupedFares = useMemo(() => {
    const groups = [];
    const seen = new Set();

    for (const fare of fares) {
      const ct = gradeMap[fare.grade_code];
      const key = ct ? ct.name : fare.grade_name;
      if (seen.has(key)) {
        // Add this grade to existing group
        const group = groups.find((g) => g.key === key);
        if (group && !group.fares.find((f) => f.grade_code === fare.grade_code)) {
          group.fares.push(fare);
        }
        continue;
      }
      seen.add(key);
      groups.push({
        key,
        cabinType: ct || null,
        fares: [fare],
      });
    }
    return groups;
  }, [fares, gradeMap]);

  const [expandedGroup, setExpandedGroup] = useState(null);

  if (fares.length === 0 && detailStatus !== "loading") {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Select Cabin Options</h2>
        <div className="text-center py-8 text-gray-400">
          <p className="text-sm">No cabin data available for this cruise.</p>
          <p className="text-xs mt-1">You can add custom cabin options instead.</p>
        </div>
        <button onClick={onAddCustom} className="text-sm text-indigo-600 font-medium hover:text-indigo-700">
          + Add custom cabin option
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Select Cabin Options</h2>
      <p className="text-sm text-gray-500 mb-5">
        Choose the cabin categories to include in your quote. Set your own pricing below.
      </p>

      {detailStatus === "loading" && (
        <div className="flex items-center gap-2 text-sm text-indigo-500 mb-4">
          <div className="animate-spin h-4 w-4 border-2 border-indigo-400 border-t-transparent rounded-full" />
          Loading cabin details...
        </div>
      )}

      <div className="space-y-3">
        {groupedFares.map((group) => {
          const ct = group.cabinType;
          const isExpanded = expandedGroup === group.key;
          const anySelected = group.fares.some((f) =>
            selectedCabins.some((c) => c.gradeCode === f.grade_code && c.gradeName === f.grade_name)
          );

          return (
            <div
              key={group.key}
              className={`border-2 rounded-xl overflow-hidden transition-all ${
                anySelected ? "border-indigo-500 bg-indigo-50/30" : "border-gray-200"
              }`}
            >
              {/* Cabin type header with image */}
              <div
                className="cursor-pointer"
                onClick={() => setExpandedGroup(isExpanded ? null : group.key)}
              >
                <div className="flex gap-4 p-4">
                  {ct?.imageUrl && (
                    <img
                      src={ct.imageUrl}
                      alt={ct.name}
                      className="h-20 w-28 rounded-lg object-cover flex-shrink-0"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">
                          {ct?.name || group.key}
                        </h3>
                        {ct?.type && (
                          <span className="inline-block mt-0.5 px-2 py-0.5 bg-gray-100 rounded text-xs font-medium text-gray-600">
                            {ct.type}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {anySelected && (
                          <span className="text-xs font-semibold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                            Selected
                          </span>
                        )}
                        <ChevronDownIcon
                          className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        />
                      </div>
                    </div>
                    {/* Quick stats */}
                    <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-gray-500">
                      {ct?.minSize && (
                        <span>{ct.minSize}{ct.maxSize && ct.maxSize !== ct.minSize ? `–${ct.maxSize}` : ""} sq ft</span>
                      )}
                      {ct?.maxOccupancy && (
                        <span>Up to {ct.maxOccupancy} guests</span>
                      )}
                      <span className="text-gray-400">
                        {group.fares.length} grade{group.fares.length !== 1 ? "s" : ""}: {group.fares.map((f) => f.grade_code).join(", ")}
                      </span>
                    </div>
                    {/* Short description preview */}
                    {ct?.description && !isExpanded && (
                      <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{ct.description}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="border-t border-gray-100 px-4 pb-4">
                  {/* Full description & facilities */}
                  {ct && (
                    <div className="pt-3 pb-3">
                      {ct.description && (
                        <p className="text-sm text-gray-600 leading-relaxed mb-3">{ct.description}</p>
                      )}
                      {ct.facilities?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {ct.facilities.map((f, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-md text-xs text-gray-600">
                              <CheckIcon className="h-3 w-3 text-green-500" />
                              {f}
                            </span>
                          ))}
                        </div>
                      )}
                      {ct.floorplanUrl && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Floor Plan</p>
                          <img
                            src={ct.floorplanUrl}
                            alt="Floor plan"
                            className="max-h-40 rounded-lg border border-gray-200"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Grade options to select */}
                  <div className="space-y-2 pt-2 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Select grades to quote</p>
                    {group.fares.map((fare, fIdx) => {
                      const isSelected = selectedCabins.some(
                        (c) => c.gradeCode === fare.grade_code && c.gradeName === fare.grade_name
                      );
                      return (
                        <div
                          key={fIdx}
                          onClick={() => onToggleFare(fare, primaryFareSet?.name || "")}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                            isSelected
                              ? "bg-indigo-100 border border-indigo-300"
                              : "bg-gray-50 hover:bg-gray-100 border border-transparent"
                          }`}
                        >
                          <div
                            className={`h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? "bg-indigo-600 border-indigo-600" : "border-gray-300"
                            }`}
                          >
                            {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{fare.grade_name}</p>
                            <p className="text-xs text-gray-500">Category {fare.grade_code}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            {fare.availability === "closed" ? (
                              <span className="text-xs text-amber-600 font-medium">Limited</span>
                            ) : (
                              <span className="text-xs text-green-600 font-medium">Available</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Manual cabin add */}
      <div className="mt-5 pt-4 border-t border-gray-100">
        <button onClick={onAddCustom} className="text-sm text-indigo-600 font-medium hover:text-indigo-700">
          + Add custom cabin option
        </button>
      </div>
    </div>
  );
}

export default function QuoteBuilder() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditing = Boolean(editId);

  const {
    list: cruises,
    total: totalCruises,
    totalPages,
    operators,
    operatorsStatus,
    cruiseDetail,
    detailStatus,
    status: cruiseStatus,
  } = useSelector((state) => state.cruises);
  const { createStatus } = useSelector((state) => state.quotes);
  const { agent } = useSelector((state) => state.auth);

  const [step, setStep] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [searchParams, setSearchParams] = useState({
    operator: "all",
    ship_name: "",
    region: "",
    start_date_from: "",
    start_date_to: "",
    embarkation_port: "",
    page: 1,
  });

  // Selected cruise from Widgety
  const [selectedCruise, setSelectedCruise] = useState(null);
  // Selected cabin/fare options
  const [selectedCabins, setSelectedCabins] = useState([]);
  // Client info
  const [client, setClient] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  // Quote settings
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const PRICING_DISCLAIMER = "Prices and availability are subject to change without notice and are not guaranteed until a deposit has been received and the booking is confirmed by the cruise line. All fares are per person based on double occupancy unless otherwise noted. Taxes, fees, and port charges may apply.";

  // Operator logo map
  const operatorLogos = useMemo(() => {
    const map = {};
    operators.forEach((op) => { if (op.logoUrl) map[op.id] = op.logoUrl; });
    return map;
  }, [operators]);

  // Ships for selected operator
  const shipsForOperator = useMemo(() => {
    if (searchParams.operator === "all") return [];
    const op = operators.find((o) => o.id === searchParams.operator);
    return op?.ships || [];
  }, [operators, searchParams.operator]);

  // Load operators on mount
  useEffect(() => {
    if (operatorsStatus === "idle") {
      dispatch(fetchOperators());
    }
  }, [dispatch, operatorsStatus]);

  // Load existing quote for editing
  useEffect(() => {
    if (editId) {
      dispatch(fetchQuote(editId)).then((action) => {
        if (action.payload) {
          const q = action.payload;
          setSelectedCruise({
            ref: q.sailing.ref,
            name: q.sailing.product,
            shipTitle: q.sailing.ship,
            operatorTitle: q.sailing.cruiseLine,
            startsAt: q.sailing.startsAt || "",
            endsAt: q.sailing.endsAt || "",
            startsOn: q.sailing.sailDate,
            cruiseNights: q.sailing.nights,
            regions: q.sailing.region ? [q.sailing.region] : [],
          });
          setSelectedCabins(q.cabinSelections);
          setClient(q.client);
          setNotes(q.notes || "");
          setInternalNotes(q.internalNotes || "");
          setCustomMessage(q.customMessage || "");
          setStep(3);
        }
      });
    }
  }, [editId, dispatch]);

  // Search cruises - only called on explicit action (button click or pagination)
  const handleSearch = () => {
    setSearchParams((prev) => ({ ...prev, page: 1 }));
    const params = { ...searchParams, page: 1 };
    Object.keys(params).forEach((k) => {
      if (!params[k] || params[k] === "all") delete params[k];
    });
    dispatch(searchCruises(params));
  };

  const handlePageChange = (newPage) => {
    setSearchParams((prev) => ({ ...prev, page: newPage }));
    const params = { ...searchParams, page: newPage };
    Object.keys(params).forEach((k) => {
      if (!params[k] || params[k] === "all") delete params[k];
    });
    dispatch(searchCruises(params));
  };

  const updateSearch = (field, value) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: value,
      // Reset ship when operator changes
      ...(field === "operator" ? { ship_name: "" } : {}),
    }));
  };

  // Select a cruise and load details
  const handleSelectCruise = (cruise) => {
    setSelectedCruise(cruise);
    dispatch(clearCruiseDetail());
    if (cruise.ref) {
      dispatch(fetchCruiseDetail({ ref: cruise.ref, shipId: cruise.shipId }));
    }
    setStep(1);
  };

  // Toggle fare selection
  const toggleFare = (fare, fareSetName) => {
    const exists = selectedCabins.find((c) => c.gradeCode === fare.grade_code && c.gradeName === fare.grade_name);
    if (exists) {
      setSelectedCabins(selectedCabins.filter(
        (c) => !(c.gradeCode === fare.grade_code && c.gradeName === fare.grade_name)
      ));
    } else {
      // Look up cabin type info from cruise detail
      const ct = (cruiseDetail?.cabinTypes || []).find(
        (t) => t.gradeCodes?.includes(fare.grade_code)
      );
      setSelectedCabins([
        ...selectedCabins,
        {
          gradeCode: fare.grade_code,
          gradeName: fare.grade_name,
          categoryName: ct?.name || fare.grade_name,
          category: fare.grade_code,
          categoryClass: ct?.type || fare.grade_code.charAt(0),
          pricePerPerson: 0,
          originalPrice: 0,
          savings: 0,
          savingsPercent: 0,
          currency: "CAD",
          numGuests: 2,
          isRecommended: false,
          fareSetName: fareSetName || "",
          cabinImage: ct?.imageUrl || null,
          cabinDescription: ct?.description || "",
          cabinSize: ct?.minSize || null,
          cabinMaxOccupancy: ct?.maxOccupancy || null,
          cabinFacilities: ct?.facilities || [],
        },
      ]);
    }
  };

  const updateCabinField = (index, field, value) => {
    const updated = [...selectedCabins];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedCabins(updated);
  };

  const setRecommended = (index) => {
    setSelectedCabins(selectedCabins.map((c, i) => ({ ...c, isRecommended: i === index })));
  };

  const removeCabin = (index) => {
    setSelectedCabins(selectedCabins.filter((_, i) => i !== index));
  };

  // Submit quote
  const handleSubmit = async () => {
    const quoteData = {
      client,
      sailing: {
        ref: selectedCruise.ref,
        cruiseLine: selectedCruise.operatorTitle,
        ship: selectedCruise.shipTitle,
        sailDate: selectedCruise.startsOn,
        returnDate: selectedCruise.endsOn,
        product: selectedCruise.name,
        nights: selectedCruise.cruiseNights,
        region: selectedCruise.regions?.[0] || "",
        startsAt: selectedCruise.startsAt,
        endsAt: selectedCruise.endsAt,
        cruiseType: selectedCruise.cruiseType?.[0] || "Ocean",
        rating: selectedCruise.rating || "",
      },
      cabinSelections: selectedCabins,
      notes,
      internalNotes,
      customMessage,
      pricingDisclaimer: PRICING_DISCLAIMER,
    };

    let result;
    if (isEditing) {
      result = await dispatch(updateQuote({ id: editId, updates: quoteData }));
    } else {
      result = await dispatch(createQuote(quoteData));
    }

    if (result.payload?._id) {
      navigate(`/quotes/${result.payload._id}`);
    } else {
      navigate("/quotes");
    }
  };

  const canProceed = () => {
    if (step === 0) return !!selectedCruise;
    if (step === 1) return selectedCabins.length > 0;
    if (step === 2) return client.firstName && client.lastName && client.email;
    return true;
  };

  const activeFilters = [
    searchParams.operator !== "all" && searchParams.operator,
    searchParams.ship_name,
    searchParams.region,
    searchParams.start_date_from,
    searchParams.embarkation_port,
  ].filter(Boolean).length;

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">
        {isEditing ? "Edit Quote" : "Create New Quote"}
      </h1>

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <button
              onClick={() => i < step && setStep(i)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                i === step
                  ? "bg-indigo-600 text-white"
                  : i < step
                  ? "bg-indigo-100 text-indigo-700 cursor-pointer hover:bg-indigo-200"
                  : "bg-gray-100 text-gray-400"
              }`}
            >
              {i < step ? <CheckIcon className="h-4 w-4" /> : <span>{i + 1}</span>}
              <span className="hidden sm:inline">{s}</span>
            </button>
            {i < STEPS.length - 1 && <ChevronRightIcon className="h-4 w-4 text-gray-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* ===== STEP 0: CRUISE SEARCH ===== */}
      {step === 0 && (
        <div className="space-y-4">
          {/* Search bar */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-4">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <h2 className="text-lg font-semibold text-gray-900">Find a Cruise</h2>
              <span className="text-sm text-gray-500">
                {totalCruises > 0 && `${totalCruises.toLocaleString()} results`}
              </span>
            </div>

            {/* Primary filters row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              {/* Cruise Line */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Cruise Line</label>
                <select
                  value={searchParams.operator}
                  onChange={(e) => updateSearch("operator", e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                >
                  <option value="all">All Cruise Lines</option>
                  {operators.map((op) => (
                    <option key={op.id} value={op.id}>{op.title}</option>
                  ))}
                </select>
              </div>

              {/* Ship */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Ship</label>
                {shipsForOperator.length > 0 ? (
                  <select
                    value={searchParams.ship_name}
                    onChange={(e) => updateSearch("ship_name", e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  >
                    <option value="">All Ships</option>
                    {shipsForOperator.map((s) => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={searchParams.ship_name}
                    onChange={(e) => updateSearch("ship_name", e.target.value)}
                    placeholder="Any ship..."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>

              {/* Destination */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Destination</label>
                <select
                  value={searchParams.region}
                  onChange={(e) => updateSearch("region", e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                >
                  <option value="">All Destinations</option>
                  {REGIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              {/* Departure Date From */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Departure From</label>
                <input
                  type="date"
                  value={searchParams.start_date_from}
                  onChange={(e) => updateSearch("start_date_from", e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Search button + advanced toggle */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 font-medium"
              >
              <AdjustmentsHorizontalIcon className="h-4 w-4" />
              {showFilters ? "Hide" : "More"} Filters
              {activeFilters > 0 && (
                <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {activeFilters}
                </span>
              )}
              <ChevronDownIcon className={`h-3 w-3 transition-transform ${showFilters ? "rotate-180" : ""}`} />
              </button>

              <button
                onClick={handleSearch}
                disabled={cruiseStatus === "loading"}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
                {cruiseStatus === "loading" ? "Searching..." : "Search Cruises"}
              </button>
            </div>

            {/* Advanced filters */}
            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Departure To</label>
                  <input
                    type="date"
                    value={searchParams.start_date_to}
                    onChange={(e) => updateSearch("start_date_to", e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Embarkation Port</label>
                  <input
                    type="text"
                    value={searchParams.embarkation_port}
                    onChange={(e) => updateSearch("embarkation_port", e.target.value)}
                    placeholder="e.g. Southampton, Miami..."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() =>
                      setSearchParams({
                        operator: "all",
                        ship_name: "",
                        region: "",
                        start_date_from: "",
                        start_date_to: "",
                        embarkation_port: "",
                        page: 1,
                      })
                    }
                    className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          {cruiseStatus === "loading" ? (
            <div className="text-center py-16">
              <div className="animate-spin h-10 w-10 border-3 border-indigo-600 border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-sm text-gray-500">Searching cruises...</p>
            </div>
          ) : cruises.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <GlobeAltIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No cruises found</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your search filters</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {cruises.map((cruise) => (
                  <CruiseCard
                    key={cruise.ref}
                    cruise={cruise}
                    onSelect={handleSelectCruise}
                    operatorLogos={operatorLogos}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-3 mt-6">
                  <button
                    onClick={() => handlePageChange(Math.max(1, searchParams.page - 1))}
                    disabled={searchParams.page <= 1}
                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-500">
                    Page {searchParams.page} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, searchParams.page + 1))}
                    disabled={searchParams.page >= totalPages}
                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== STEP 1: CABIN & PRICING ===== */}
      {step === 1 && (
        <div className="space-y-4">
          {/* Selected cruise summary */}
          <div className="bg-indigo-50 rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                  {selectedCruise?.operatorTitle}
                </p>
                <h3 className="text-lg font-bold text-indigo-900 mt-0.5">{selectedCruise?.shipTitle}</h3>
                <p className="text-sm text-indigo-700 mt-1">{selectedCruise?.name}</p>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-indigo-600">
                  <span className="inline-flex items-center gap-1">
                    <CalendarDaysIcon className="h-3.5 w-3.5" />
                    {formatDate(selectedCruise?.startsOn)}
                  </span>
                  <span>{selectedCruise?.cruiseNights} nights</span>
                  {selectedCruise?.startsAt && (
                    <span className="inline-flex items-center gap-1">
                      <MapPinIcon className="h-3.5 w-3.5" />
                      {selectedCruise.startsAt}
                      {selectedCruise.endsAt && selectedCruise.endsAt !== selectedCruise.startsAt && ` → ${selectedCruise.endsAt}`}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => { setStep(0); setSelectedCabins([]); }} className="text-sm text-indigo-600 font-medium hover:underline flex-shrink-0">
                Change
              </button>
            </div>

            {/* Port visits itinerary */}
            {detailStatus === "loading" && (
              <div className="mt-4 flex items-center gap-2 text-xs text-indigo-500">
                <div className="animate-spin h-4 w-4 border-2 border-indigo-400 border-t-transparent rounded-full" />
                Loading itinerary...
              </div>
            )}
            {cruiseDetail?.portVisits && <PortVisitTimeline portVisits={cruiseDetail.portVisits} />}
          </div>

          {/* Cabin selection with rich detail */}
          <CabinSelector
            fareSets={selectedCruise?.fareSets || []}
            cabinTypes={cruiseDetail?.cabinTypes || []}
            selectedCabins={selectedCabins}
            onToggleFare={toggleFare}
            onAddCustom={() => {
              setSelectedCabins([
                ...selectedCabins,
                {
                  gradeCode: "",
                  gradeName: "",
                  categoryName: "Custom Cabin",
                  category: "",
                  categoryClass: "",
                  pricePerPerson: 0,
                  originalPrice: 0,
                  savings: 0,
                  savingsPercent: 0,
                  currency: "CAD",
                  numGuests: 2,
                  isRecommended: false,
                  fareSetName: "",
                  isCustom: true,
                },
              ]);
            }}
            detailStatus={detailStatus}
          />

          {/* Selected cabins edit panel */}
          {selectedCabins.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Selected Options ({selectedCabins.length})
              </h3>
              <div className="space-y-3">
                {selectedCabins.map((cabin, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-start gap-3 mb-3">
                      {cabin.cabinImage && (
                        <img
                          src={cabin.cabinImage}
                          alt={cabin.categoryName}
                          className="h-16 w-24 rounded-lg object-cover flex-shrink-0"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            {cabin.isCustom ? (
                              <input
                                type="text"
                                value={cabin.categoryName}
                                onChange={(e) => updateCabinField(idx, "categoryName", e.target.value)}
                                placeholder="Cabin name (e.g. Ocean View Stateroom)"
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                              />
                            ) : (
                              <>
                                <p className="font-semibold text-sm text-gray-900">{cabin.categoryName}</p>
                                <p className="text-xs text-gray-500">Category {cabin.gradeCode}</p>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => setRecommended(idx)}
                              className={`p-1.5 rounded-lg ${cabin.isRecommended ? "text-yellow-500 bg-yellow-50" : "text-gray-300 hover:text-yellow-400"}`}
                              title="Mark as recommended"
                            >
                              {cabin.isRecommended ? <StarSolid className="h-5 w-5" /> : <StarIcon className="h-5 w-5" />}
                            </button>
                            <button
                              onClick={() => removeCabin(idx)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Price/person *</label>
                        <input
                          type="number"
                          min={0}
                          value={cabin.pricePerPerson || ""}
                          onChange={(e) => updateCabinField(idx, "pricePerPerson", parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Currency</label>
                        <select
                          value={cabin.currency}
                          onChange={(e) => updateCabinField(idx, "currency", e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
                        >
                          <option value="CAD">CAD (C$)</option>
                          <option value="USD">USD ($)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Guests</label>
                        <input
                          type="number"
                          min={1}
                          max={10}
                          value={cabin.numGuests}
                          onChange={(e) => updateCabinField(idx, "numGuests", parseInt(e.target.value) || 2)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== STEP 2: CLIENT DETAILS ===== */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                value={client.firstName}
                onChange={(e) => setClient({ ...client, firstName: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                value={client.lastName}
                onChange={(e) => setClient({ ...client, lastName: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Smith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={client.email}
                onChange={(e) => setClient({ ...client, email: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={client.phone}
                onChange={(e) => setClient({ ...client, phone: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <hr className="my-6" />

          <h3 className="text-md font-semibold text-gray-900 mb-3">Quote Options</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Personal Message to Client</label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Hi John, I found some great options for your anniversary trip..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (visible to client)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Prices include all taxes and port fees..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes (only you can see)</label>
              <textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Client prefers higher deck, flexible on dates..."
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">Pricing Disclaimer</p>
              <p className="text-xs text-amber-700 leading-relaxed">{PRICING_DISCLAIMER}</p>
              <p className="text-xs text-amber-600 mt-1 italic">This disclaimer will appear on all client-facing quotes.</p>
            </div>
          </div>
        </div>
      )}

      {/* ===== STEP 3: REVIEW ===== */}
      {step === 3 && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Review Your Quote</h2>

            {/* Sailing */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 mb-4">
              <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                {selectedCruise?.operatorTitle}
              </p>
              <h3 className="text-xl font-bold text-gray-900 mt-1">{selectedCruise?.shipTitle}</h3>
              <p className="text-sm text-gray-600 mt-1">{selectedCruise?.name}</p>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-600">
                <span>{formatDate(selectedCruise?.startsOn)}</span>
                <span>{selectedCruise?.cruiseNights} nights</span>
                {selectedCruise?.startsAt && (
                  <span>{selectedCruise.startsAt} → {selectedCruise.endsAt}</span>
                )}
                {selectedCruise?.regions?.[0] && (
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                    {selectedCruise.regions[0]}
                  </span>
                )}
              </div>
            </div>

            {/* Client */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Client</h3>
              <p className="text-gray-900">{client.firstName} {client.lastName}</p>
              <p className="text-sm text-gray-600">{client.email}</p>
              {client.phone && <p className="text-sm text-gray-600">{client.phone}</p>}
            </div>

            {/* Cabin Options */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Cabin Options ({selectedCabins.length})
              </h3>
              <div className="space-y-2">
                {selectedCabins.map((cabin, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 text-sm">{cabin.categoryName}</span>
                      {cabin.isRecommended && (
                        <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                          Recommended
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900 text-sm">
                        {formatCurrency(cabin.pricePerPerson, cabin.currency)}/person
                      </p>
                      <p className="text-xs text-gray-500">
                        {cabin.numGuests} guests = {formatCurrency(cabin.pricePerPerson * cabin.numGuests, cabin.currency)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {customMessage && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">Personal Message</h3>
                <p className="text-sm text-gray-700">{customMessage}</p>
              </div>
            )}
            {notes && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">Notes</h3>
                <p className="text-sm text-gray-700">{notes}</p>
              </div>
            )}
            {internalNotes && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Internal Notes (not visible to client)
                </h3>
                <p className="text-sm text-gray-500 italic">{internalNotes}</p>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-3 mt-2">
              <p className="text-xs text-gray-500 leading-relaxed">{PRICING_DISCLAIMER}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <button
          onClick={() => (step > 0 ? setStep(step - 1) : navigate("/quotes"))}
          className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {step === 0 ? "Cancel" : "Back"}
        </button>
        <div className="flex gap-3">
          {step === 3 && (
            <button
              onClick={handleSubmit}
              disabled={createStatus === "loading"}
              className="px-6 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {createStatus === "loading" ? "Saving..." : isEditing ? "Update Quote" : "Save as Draft"}
            </button>
          )}
          {step < 3 && (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="px-6 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
